import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Alert, Button, Icon, Skeleton, TextField, Typography } from '@ann/web-components';
import { useWidgetLibrary } from '../../hooks';
import { WIDGET_LIBRARY_COPY } from '../../constants/dashboardBuilderCopy';
import { CategoryPanel, WidgetDetailView, WidgetListItem, WidgetPreviewItem } from './components';
import {
  ContentArea,
  EmptyStateContainer,
  HeaderTextBlock,
  MainPanel,
  ModalContainer,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  SelectionAlertWrap,
  Overlay,
  SearchWrapper,
  SkeletonGrid,
  Toolbar,
  ViewToggleGroup,
  WidgetGrid,
} from './WidgetLibraryModal.styled';

const SKELETON_ITEMS = Array.from({ length: 8 });

const WidgetLibraryModal = ({ open = false, onClose, onConfirm, sectionId }) => {
  const overlayRef = useRef(null);
  const selectedRef = useRef([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [expandedWidget, setExpandedWidget] = useState(null);

  const {
    widgets,
    loading,
    viewMode,
    searchTerm,
    selectedCategory,
    selectedWidgets,
    selectedWidgetObjects,
    categories,
    setSearchTerm,
    setSelectedCategory,
    toggleViewMode,
    toggleWidget,
    addWidgetFromDetail,
  } = useWidgetLibrary({ open, sectionId });

  selectedRef.current = selectedWidgetObjects;

  useEffect(() => {
    if (!open) {
      setExpandedWidget(null);
      return;
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === overlayRef.current) {
        onClose?.();
      }
    },
    [onClose]
  );

  const handleConfirm = useCallback(() => {
    onConfirm?.(selectedRef.current);
  }, [onConfirm]);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const handleExpandWidget = useCallback((widget) => {
    setExpandedWidget(widget);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setExpandedWidget(null);
  }, []);

  const handleAddFromDetail = useCallback((widget, size) => {
    addWidgetFromDetail(widget.id, size);
    setExpandedWidget(null);
  }, [addWidgetFromDetail]);

  if (!open) return null;

  const selectedWidgetNames = selectedWidgetObjects
    .map((w) => w.name)
    .filter(Boolean)
    .join(', ');

  const hasSelectedWidgets = selectedWidgets.size > 0;
  const isTextMode = viewMode === 'text';
  const skeletonHeight = isTextMode ? '88px' : '160px';

  return ReactDOM.createPortal(
    <Overlay ref={overlayRef} onClick={handleOverlayClick}>
      <ModalContainer role="dialog" aria-modal="true">
        <CategoryPanel
          categories={categories}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
          isOpen={isSidebarOpen}
          onToggle={handleToggleSidebar}
        />

        <MainPanel>
          {expandedWidget ? (
            <WidgetDetailView
              widget={expandedWidget}
              onBack={handleCloseDetail}
              onClose={onClose}
              onAdd={handleAddFromDetail}
            />
          ) : (
            <>
              <ModalHeader>
                <HeaderTextBlock>
                  <Typography category="subtitle" size="medium" weight="bold">
                    {WIDGET_LIBRARY_COPY.title}
                  </Typography>
                  <Typography category="body" size="small" weight="regular" color="secondary">
                    {WIDGET_LIBRARY_COPY.description}
                  </Typography>
                </HeaderTextBlock>
                <Button
                  type="tertiary"
                  size="small"
                  iconLeft="close"
                  onClick={onClose}
                />
              </ModalHeader>

              <Toolbar>
                <SearchWrapper>
                  <TextField
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder={WIDGET_LIBRARY_COPY.searchPlaceholder}
                    iconLeft="search"
                    size="small"
                  />
                </SearchWrapper>
                <ViewToggleGroup>
                  <Button
                    type={isTextMode ? 'primary' : 'secondary'}
                    size="small"
                    icon="densitySmall"
                    onClick={isTextMode ? undefined : toggleViewMode}
                    title={WIDGET_LIBRARY_COPY.viewModeListTitle}
                  />
                  <Button
                    type={!isTextMode ? 'primary' : 'secondary'}
                    size="small"
                    icon="image"
                    onClick={isTextMode ? toggleViewMode : undefined}
                    title={WIDGET_LIBRARY_COPY.viewModeGridTitle}
                  />
                </ViewToggleGroup>
              </Toolbar>

              <ContentArea>
                {loading ? (
                  <SkeletonGrid>
                    {SKELETON_ITEMS.map((_, i) => (
                      <Skeleton
                        key={`widget-skeleton-${i}`}
                        height={skeletonHeight}
                        width="100%"
                        borderRadius="12px"
                      />
                    ))}
                  </SkeletonGrid>
                ) : widgets.length === 0 ? (
                  <EmptyStateContainer>
                    <Icon name="search" size="large" color="neutral" />
                    <Typography category="body" size="medium" weight="medium">
                      {WIDGET_LIBRARY_COPY.emptySearch}
                    </Typography>
                    <Typography category="body" size="small" weight="regular" color="secondary">
                      {WIDGET_LIBRARY_COPY.emptySearchDescription}
                    </Typography>
                  </EmptyStateContainer>
                ) : (
                  <WidgetGrid>
                    {widgets.map((widget) =>
                      isTextMode ? (
                        <WidgetListItem
                          key={widget.id}
                          widget={widget}
                          widgetId={widget.id}
                          isSelected={selectedWidgets.has(widget.id)}
                          onToggle={toggleWidget}
                        />
                      ) : (
                        <WidgetPreviewItem
                          key={widget.id}
                          widget={widget}
                          widgetId={widget.id}
                          isSelected={selectedWidgets.has(widget.id)}
                          onToggle={toggleWidget}
                          onExpand={handleExpandWidget}
                        />
                      )
                    )}
                  </WidgetGrid>
                )}
              </ContentArea>

              <ModalFooter>
                {hasSelectedWidgets && (
                  <SelectionAlertWrap>
                    <Alert
                      severity="info"
                      title={WIDGET_LIBRARY_COPY.selectedAlertTitle(selectedWidgets.size)}
                      description={selectedWidgetNames}
                      closable={false}
                      position="relative"
                      maxWidth="100%"
                      noShadow
                    />
                  </SelectionAlertWrap>
                )}
                <ModalFooterActions>
                  <Button
                    type="secondary"
                    size="medium"
                    label={WIDGET_LIBRARY_COPY.cancelButton}
                    onClick={onClose}
                  />
                  <Button
                    type="primary"
                    size="medium"
                    label={WIDGET_LIBRARY_COPY.addButton}
                    disabled={!hasSelectedWidgets}
                    onClick={handleConfirm}
                  />
                </ModalFooterActions>
              </ModalFooter>
            </>
          )}
        </MainPanel>
      </ModalContainer>
    </Overlay>,
    document.body
  );
};

export default memo(WidgetLibraryModal);
